import { PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';
import { Link } from '@inertiajs/react';
import { Icon } from '../Icon';
import { ClassName } from '@/types';

type MenuItemProps = PropsWithChildren<{
    href: string;
    icon: string;
    isActive?: boolean;
    className?: ClassName;
}>;

export const MenuItem = ({ href, icon, isActive = false, children, className }: MenuItemProps)=> {
    return (
        <Link href={href} className={twMerge('w-full grow md:grow-0', className)}>
            <PillMenuItem
                icon={icon}
                isActive={isActive ?? false}>
                {children}
            </PillMenuItem>

            <FilledMenuItem
                icon={icon}
                isActive={isActive ?? false}>
                {children}
            </FilledMenuItem>
        </Link>
    );
}

function PillMenuItem({ icon, isActive, children }: PropsWithChildren<{ icon: string; isActive: boolean; }>) {
    return (
        <div
            className={twMerge(
                'group flex lg:hidden flex-col items-center text-on-surface-variant',
                isActive && 'font-semibold text-on-secondary-container'
            )}
        >
            <div className={'relative h-8 w-16'}>
                {/* Background with Hover-Opacity-transition */}
                <div
                    className={'absolute inset-0 rounded-full bg-on-surface-variant/0 transition-opacity duration-200 group-hover:bg-on-surface-variant/5 group-active:bg-on-surface-variant/10'} />

                {/* Background with Grow/Shrink-transition */}
                {isActive && (
                    <div className={'absolute inset-0 rounded-full bg-secondary-container'} />
                )}

                {/* Icon */}
                <Icon
                    name={icon}
                    filled={isActive}
                    className={'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xl'}
                />
            </div>

            {/* Content */}
            <div
                className={twMerge(
                    'grow text-center text-sm lg:text-base',
                    isActive && 'font-semibold text-on-surface'
                )}
            >
                {children}
            </div>
        </div>
    );
}

function FilledMenuItem({ icon, isActive, children }: PropsWithChildren<{ icon: string; isActive: boolean; }>) {
    return (
        <div className={'group relative hidden lg:flex h-12'}>
            {/* Background with Hover-Opacity-transition */}
            <div
                className={'absolute inset-0 rounded-full bg-on-surface-variant/0 transition-opacity duration-200 group-hover:bg-on-surface-variant/5 group-active:bg-on-surface-variant/10'} />

            {/* Background with Grow/Shrink-transition */}
            {isActive && (
                <div className={'absolute inset-0 rounded-full bg-secondary-container'} />
            )}

            {/* Content */}
            <div
                className={twMerge(
                    'absolute inset-0 flex flex-row items-center gap-1.5 px-3 py-2 text-on-surface-variant',
                    isActive && 'text-on-secondary-container'
                )}
            >
                <Icon name={icon} filled={isActive} className={'text-xl'} />

                <div className={twMerge('text-sm', isActive && 'font-semibold')}>{children}</div>
            </div>
        </div>
    );
}
